import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Eye, Check, Trash2, Archive, Rocket, History } from "lucide-react";
import {
  adminCreateDraft, adminGetListing, adminPatchListing,
  adminTrashListing, adminFetchTaxonomy,
} from "../../lib/adminApi";
import { Field, inputCls, textareaCls, AutosaveIndicator, AiAssistButton, ImageUploader } from "./parts";

const REGIONS = ["transylvania","bucovina","maramures","banat","muntenia","oltenia","moldova","dobrogea","crisana"];

const STEP_LABELS = [
  { n: 1, en: "What", ro: "Ce" },
  { n: 2, en: "Where", ro: "Unde" },
  { n: 3, en: "Basics", ro: "Bază" },
  { n: 4, en: "Contact", ro: "Contact" },
  { n: 5, en: "Images", ro: "Imagini" },
  { n: 6, en: "Description", ro: "Descriere" },
  { n: 7, en: "Pricing", ro: "Preț" },
  { n: 8, en: "SEO", ro: "SEO" },
  { n: 9, en: "Publish", ro: "Publică" },
];

const TYPE_CATEGORY_LABELS = {
  accommodation: "Accommodation", food: "Food", attraction: "Attraction",
  nature: "Nature", activity: "Activity", other: "Other",
};

const TYPE_LABEL = (t) => t.replaceAll("_"," ").replace(/\b\w/g, c => c.toUpperCase());

function toLines(v) { return (v || "").split("\n").map(s => s.trim()).filter(Boolean); }
function fromLines(v) { return (v || []).join("\n"); }
function localMirrorKey(id) { return `wr_wizard_${id}`; }

export default function Wizard() {
  const { id: routeId } = useParams();
  const nav = useNavigate();
  const [id, setId] = useState(routeId || null);
  const [step, setStep] = useState(1);
  const [record, setRecord] = useState(null);
  const [taxonomy, setTaxonomy] = useState({ listing_types: [], type_categories: {} });
  const [saveState, setSaveState] = useState("idle"); // idle|saving|saved|unsaved|offline
  const dirtyRef = useRef(null); // pending patch
  const timerRef = useRef(null);

  // Bootstrap
  useEffect(() => {
    (async () => {
      try {
        const tax = await adminFetchTaxonomy();
        setTaxonomy(tax);
      } catch { /* taxonomy is optional */ }
      if (routeId) {
        try {
          const r = await adminGetListing(routeId);
          setRecord(r); setId(r.id);
        } catch { toast.error("Listing not found"); nav("/admin/listings"); }
      } else {
        try {
          const d = await adminCreateDraft();
          setRecord(d); setId(d.id);
          nav(`/admin/edit/${d.id}`, { replace: true });
        } catch { toast.error("Could not start a draft"); nav("/admin"); }
      }
    })();
  }, [routeId, nav]);

  // Autosave — debounce 800ms + localStorage mirror
  const flushSave = useCallback(async () => {
    if (!id || !dirtyRef.current) return null;
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const payload = dirtyRef.current;
    dirtyRef.current = null;
    setSaveState("saving");
    try {
      const updated = await adminPatchListing(id, payload);
      setRecord(updated);
      setSaveState("saved");
      try { localStorage.removeItem(localMirrorKey(id)); } catch { /* ignore */ }
      setTimeout(() => setSaveState(s => s === "saved" ? "idle" : s), 1200);
      return updated;
    } catch (e) {
      // Put the payload back so it isn't lost — user can retry
      dirtyRef.current = { ...(payload || {}), ...(dirtyRef.current || {}) };
      setSaveState(navigator.onLine ? "unsaved" : "offline");
      throw e;
    }
  }, [id]);

  const scheduleSave = useCallback((patch) => {
    if (!id) return;
    dirtyRef.current = { ...(dirtyRef.current || {}), ...patch };
    setSaveState("unsaved");
    try {
      const cur = JSON.parse(localStorage.getItem(localMirrorKey(id)) || "{}");
      localStorage.setItem(localMirrorKey(id), JSON.stringify({ ...cur, ...patch }));
    } catch { /* localStorage not available in this browser */ }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { flushSave().catch(()=>{}); }, 800);
  }, [id, flushSave]);

  // Save on unmount — attempt one last flush (fire-and-forget; browsers may cancel).
  useEffect(() => {
    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      // Fire pending patch without awaiting — best effort during unmount
      if (id && dirtyRef.current) {
        const payload = dirtyRef.current;
        dirtyRef.current = null;
        adminPatchListing(id, payload).catch(()=>{});
      }
    };
  }, [id]);

  // Also flush on tab close / hide — covers the "close tab quickly" case
  useEffect(() => {
    const onHide = () => {
      if (!id || !dirtyRef.current) return;
      // localStorage mirror is already in sync; the server flush is best-effort
      const payload = dirtyRef.current;
      adminPatchListing(id, payload).catch(()=>{});
    };
    window.addEventListener("beforeunload", onHide);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") onHide(); });
    return () => { window.removeEventListener("beforeunload", onHide); };
  }, [id]);

  const setField = (k, v) => { setRecord(r => ({ ...(r||{}), [k]: v })); scheduleSave({ [k]: v }); };
  const setFields = (obj) => { setRecord(r => ({ ...(r||{}), ...obj })); scheduleSave(obj); };

  // ---- Publish / archive / delete ----
  const publish = async () => {
    // Flush any pending autosave first so the newest hero/gallery/etc lands in DB
    // before we validate + publish.
    let eff = record;
    try { const flushed = await flushSave(); if (flushed) eff = flushed; } catch { toast.error("Save failed — please retry before publishing"); return; }
    const missing = [];
    if (!eff?.name_en) missing.push("Name EN");
    if (!eff?.name_ro) missing.push("Name RO");
    if (!eff?.listing_type) missing.push("Type");
    if (!eff?.region_slug) missing.push("Region");
    if (!eff?.hero_image_url) missing.push("Hero image");
    if (missing.length) { toast.error(`Missing: ${missing.join(", ")}`); return; }
    try {
      const updated = await adminPatchListing(id, { status: "published" });
      setRecord(updated); toast.success("Published");
      nav("/admin/listings");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
  };
  const archive = async () => {
    try { await flushSave(); } catch { /* proceed; user can retry */ }
    try { const u = await adminPatchListing(id, { status: "archived" }); setRecord(u); toast.success("Archived"); }
    catch { toast.error("Archive failed"); }
  };
  const saveDraft = async () => {
    try { await flushSave(); } catch { /* proceed */ }
    try { const u = await adminPatchListing(id, { status: "draft" }); setRecord(u); toast.success("Saved as draft"); }
    catch { toast.error("Save failed"); }
  };
  const remove = async () => {
    if (!window.confirm("Move this listing to Trash?\n\nIt will be hidden from the public site but recoverable from the Trash view for permanent deletion later.")) return;
    try { await flushSave(); } catch { /* trashing supersedes the pending edit */ }
    try { await adminTrashListing(id); try { localStorage.removeItem(localMirrorKey(id)); } catch { /* ignore */ } toast.success("Moved to Trash"); nav("/admin/listings"); }
    catch { toast.error("Trash failed"); }
  };

  if (!record) {
    return <div className="max-w-[1100px] mx-auto px-6 py-24 text-[var(--wr-ink-2)]">Loading…</div>;
  }

  const context = {
    listing_type: record.listing_type, region_slug: record.region_slug,
    city: record.city, name: record.name_en || record.name_ro,
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10" data-testid="publisher-wizard">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)]">/ PUBLISHER · {routeId ? "EDIT" : "NEW"}</p>
          <h1 className="font-editorial text-4xl mt-2 leading-tight">
            {record.name_en || record.name_ro || (record.status === "draft" ? "Untitled draft" : "Untitled")}
          </h1>
        </div>
        <div className="text-right space-y-2 shrink-0">
          <AutosaveIndicator state={saveState} />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={async () => { try { await flushSave(); } catch { /* proceed */ } window.open(`/admin/history/${id}`, "_blank"); }} className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="wizard-history">
              <History size={12} /> History
            </button>
            <button type="button" onClick={async () => { try { await flushSave(); } catch { /* proceed */ } window.open(`/admin/preview/${id}`, "_blank"); }} className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="wizard-preview">
              <Eye size={12} /> Preview
            </button>
            <button type="button" onClick={async () => { try { await flushSave(); } catch { /* proceed */ } nav("/admin/listings"); }} className="inline-flex items-center gap-1.5 px-3 py-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="wizard-exit">
              Exit
            </button>
          </div>
        </div>
      </div>

      {/* Stepper rail */}
      <ol className="flex flex-wrap gap-x-3 gap-y-2 mb-8 border-y border-[var(--wr-border)] py-3" data-testid="stepper">
        {STEP_LABELS.map(s => (
          <li key={s.n}>
            <button type="button" onClick={() => setStep(s.n)}
              className={`font-mono-wr text-[10px] uppercase tracking-widest px-2 py-1 border ${step===s.n?"bg-[var(--wr-ink)] text-[var(--wr-bg)] border-[var(--wr-ink)]":"border-[var(--wr-border)] text-[var(--wr-ink-2)] hover:text-[var(--wr-ink)] hover:border-[var(--wr-ink)]"}`}
              data-testid={`step-${s.n}`}>
              {s.n}. {s.en}
            </button>
          </li>
        ))}
      </ol>

      {/* Step body */}
      <div className="min-h-[420px]" data-testid={`step-body-${step}`}>
        {step === 1 && <StepWhat taxonomy={taxonomy} record={record} setField={setField} />}
        {step === 2 && <StepWhere record={record} setField={setField} />}
        {step === 3 && <StepBasics record={record} setField={setField} context={context} />}
        {step === 4 && <StepContact record={record} setField={setField} />}
        {step === 5 && <StepImages record={record} setFields={setFields} />}
        {step === 6 && <StepDescription record={record} setField={setField} context={context} />}
        {step === 7 && <StepPricing record={record} setField={setField} />}
        {step === 8 && <StepSEO record={record} setField={setField} context={context} />}
        {step === 9 && <StepPublish record={record} onPublish={publish} onArchive={archive} onSaveDraft={saveDraft} onDelete={remove} onGoto={setStep} />}
      </div>

      {/* Footer nav */}
      <div className="mt-10 flex items-center justify-between gap-4 border-t border-[var(--wr-border)] pt-6">
        <button type="button" onClick={() => setStep(s => Math.max(1, s-1))} disabled={step===1}
          className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] disabled:opacity-40 font-mono-wr text-[10px] uppercase tracking-widest" data-testid="wizard-back">
          <ArrowLeft size={14} /> Back
        </button>
        <AutosaveIndicator state={saveState} />
        {step < 9 ? (
          <button type="button" onClick={() => setStep(s => Math.min(9, s+1))}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--wr-ink)] text-[var(--wr-bg)] hover:bg-[var(--wr-red)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="wizard-next">
            Next <ArrowRight size={14} />
          </button>
        ) : (
          <button type="button" onClick={publish}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="wizard-publish">
            <Rocket size={14} /> Publish
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- Steps ----------

function StepWhat({ taxonomy, record, setField }) {
  const cats = taxonomy.type_categories || {};
  return (
    <section className="space-y-6" data-testid="step-1-content">
      <div>
        <h2 className="font-editorial text-2xl mb-1">What are you publishing?</h2>
        <p className="text-[13px] text-[var(--wr-ink-2)]">Pick the closest type. You can change this later.</p>
      </div>
      <div className="space-y-5">
        {Object.entries(cats).map(([cat, types]) => (
          <div key={cat}>
            <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)] mb-2">{TYPE_CATEGORY_LABELS[cat] || cat}</p>
            <div className="flex flex-wrap gap-2">
              {types.map(t => (
                <button key={t} type="button" onClick={()=>setField("listing_type", t)}
                  className={`px-4 py-2.5 border font-mono-wr text-[11px] uppercase tracking-widest ${record.listing_type===t?"bg-[var(--wr-ink)] text-[var(--wr-bg)] border-[var(--wr-ink)]":"border-[var(--wr-border)] hover:border-[var(--wr-ink)]"}`}
                  data-testid={`type-${t}`}>
                  {TYPE_LABEL(t)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function StepWhere({ record, setField }) {
  return (
    <section className="space-y-6" data-testid="step-2-content">
      <div><h2 className="font-editorial text-2xl mb-1">Where is it located?</h2><p className="text-[13px] text-[var(--wr-ink-2)]">Region is required. Everything else is optional but helps travelers.</p></div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Region" required>
          <select value={record.region_slug || ""} onChange={(e)=>setField("region_slug", e.target.value)} className={inputCls} data-testid="field-region">
            <option value="">— Select —</option>
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="City"><input value={record.city || ""} onChange={(e)=>setField("city", e.target.value)} className={inputCls} data-testid="field-city" /></Field>
        <Field label="Village / locality"><input value={record.village || record.locality || ""} onChange={(e)=>{ setField("village", e.target.value); setField("locality", e.target.value); }} className={inputCls} data-testid="field-village" /></Field>
        <Field label="Postal code"><input value={record.postal_code || ""} onChange={(e)=>setField("postal_code", e.target.value)} className={inputCls} data-testid="field-postal" /></Field>
        <Field label="Address" hint="Full street address"><input value={record.address || ""} onChange={(e)=>setField("address", e.target.value)} className={inputCls} data-testid="field-address" /></Field>
        <Field label="Google Maps URL"><input value={record.google_maps_url || ""} onChange={(e)=>setField("google_maps_url", e.target.value)} className={inputCls} placeholder="https://maps.google.com/…" data-testid="field-maps-url" /></Field>
        <Field label="Latitude"><input type="number" step="0.000001" value={record.latitude ?? ""} onChange={(e)=>setField("latitude", e.target.value===""?null:Number(e.target.value))} className={inputCls} data-testid="field-lat" /></Field>
        <Field label="Longitude"><input type="number" step="0.000001" value={record.longitude ?? ""} onChange={(e)=>setField("longitude", e.target.value===""?null:Number(e.target.value))} className={inputCls} data-testid="field-lng" /></Field>
      </div>
    </section>
  );
}

function StepBasics({ record, setField, context }) {
  return (
    <section className="space-y-6" data-testid="step-3-content">
      <div><h2 className="font-editorial text-2xl mb-1">Basic information</h2><p className="text-[13px] text-[var(--wr-ink-2)]">Bilingual names and a short teaser. Everyone sees these first.</p></div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Listing name (Romanian)" required><input value={record.name_ro || ""} onChange={(e)=>setField("name_ro", e.target.value)} className={inputCls} data-testid="field-name-ro" /></Field>
        <Field label="Listing name (English)" required><input value={record.name_en || ""} onChange={(e)=>setField("name_en", e.target.value)} className={inputCls} data-testid="field-name-en" /></Field>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Short description (Romanian)</span>
            <AiAssistButton field="short_description" locale="ro" context={context} onInsert={(v)=>setField("short_description_ro", v)} testid="ai-short-ro" />
          </div>
          <textarea rows={3} value={record.short_description_ro || ""} onChange={(e)=>setField("short_description_ro", e.target.value)} className={textareaCls} data-testid="field-short-ro" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Short description (English)</span>
            <AiAssistButton field="short_description" locale="en" context={context} onInsert={(v)=>setField("short_description_en", v)} testid="ai-short-en" />
          </div>
          <textarea rows={3} value={record.short_description_en || ""} onChange={(e)=>setField("short_description_en", e.target.value)} className={textareaCls} data-testid="field-short-en" />
        </div>
      </div>
    </section>
  );
}

function StepContact({ record, setField }) {
  return (
    <section className="space-y-6" data-testid="step-4-content">
      <div><h2 className="font-editorial text-2xl mb-1">Contact</h2><p className="text-[13px] text-[var(--wr-ink-2)]">All optional. Leave empty if you&apos;d rather keep private.</p></div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Public phone"><input value={record.phone || ""} onChange={(e)=>setField("phone", e.target.value)} className={inputCls} data-testid="field-phone" /></Field>
        <Field label="Reservation phone"><input value={record.reservation_phone || ""} onChange={(e)=>setField("reservation_phone", e.target.value)} className={inputCls} data-testid="field-res-phone" /></Field>
        <Field label="Reservation email"><input type="email" value={record.reservation_email || ""} onChange={(e)=>setField("reservation_email", e.target.value)} className={inputCls} data-testid="field-res-email" /></Field>
        <Field label="Website"><input value={record.website_url || ""} onChange={(e)=>setField("website_url", e.target.value)} className={inputCls} placeholder="https://…" data-testid="field-website" /></Field>
      </div>

      <div>
        <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)] mb-3">Social &amp; promotional</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Facebook"><input value={record.facebook_url || ""} onChange={(e)=>setField("facebook_url", e.target.value)} className={inputCls} placeholder="https://facebook.com/…" data-testid="field-facebook" /></Field>
          <Field label="Instagram"><input value={record.instagram_url || ""} onChange={(e)=>setField("instagram_url", e.target.value)} className={inputCls} placeholder="https://instagram.com/…" data-testid="field-instagram" /></Field>
          <Field label="TikTok"><input value={record.tiktok_url || ""} onChange={(e)=>setField("tiktok_url", e.target.value)} className={inputCls} placeholder="https://tiktok.com/@…" data-testid="field-tiktok" /></Field>
          <Field label="YouTube channel"><input value={record.youtube_url || ""} onChange={(e)=>setField("youtube_url", e.target.value)} className={inputCls} placeholder="https://youtube.com/…" data-testid="field-youtube" /></Field>
          <Field label="Promotional video URL" hint="External only — we don't host videos"><input value={record.promo_video_url || ""} onChange={(e)=>setField("promo_video_url", e.target.value)} className={inputCls} placeholder="https://youtube.com/watch?…" data-testid="field-promo-video" /></Field>
        </div>
      </div>

      <div>
        <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)] mb-3">Booking &amp; discovery</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Google Business Profile"><input value={record.google_business_url || ""} onChange={(e)=>setField("google_business_url", e.target.value)} className={inputCls} placeholder="https://g.page/…" data-testid="field-google-business" /></Field>
          <Field label="Google Maps"><input value={record.google_maps_url || ""} onChange={(e)=>setField("google_maps_url", e.target.value)} className={inputCls} placeholder="https://maps.google.com/…" data-testid="field-google-maps" /></Field>
          <Field label="Booking.com"><input value={record.booking_com_url || ""} onChange={(e)=>setField("booking_com_url", e.target.value)} className={inputCls} placeholder="https://booking.com/hotel/…" data-testid="field-bookingcom" /></Field>
          <Field label="Airbnb"><input value={record.airbnb_url || ""} onChange={(e)=>setField("airbnb_url", e.target.value)} className={inputCls} placeholder="https://airbnb.com/rooms/…" data-testid="field-airbnb" /></Field>
          <Field label="WhatsApp"><input value={record.whatsapp_url || ""} onChange={(e)=>setField("whatsapp_url", e.target.value)} className={inputCls} placeholder="https://wa.me/…" data-testid="field-whatsapp" /></Field>
          <Field label="Waze"><input value={record.waze_url || ""} onChange={(e)=>setField("waze_url", e.target.value)} className={inputCls} placeholder="https://waze.com/ul?…" data-testid="field-waze" /></Field>
        </div>
      </div>
    </section>
  );
}

function StepImages({ record, setFields }) {
  return (
    <section className="space-y-6" data-testid="step-5-content">
      <div><h2 className="font-editorial text-2xl mb-1">Images</h2><p className="text-[13px] text-[var(--wr-ink-2)]">Drag &amp; drop, or paste a URL. Star the hero image. Drag cards to reorder.</p></div>
      <ImageUploader
        heroUrl={record.hero_image_url || ""}
        gallery={record.gallery_images || []}
        captions={record.gallery_captions || []}
        onHeroChange={(u) => setFields({ hero_image_url: u })}
        onGalleryChange={(g) => setFields({ gallery_images: g })}
        onCaptionsChange={(c) => setFields({ gallery_captions: c })}
      />
    </section>
  );
}

function StepDescription({ record, setField, context }) {
  const [amenities, setAmenities] = useState(fromLines(record.amenities));
  const [hlEn, setHlEn] = useState(fromLines(record.highlights_en));
  const [hlRo, setHlRo] = useState(fromLines(record.highlights_ro));
  useEffect(() => setAmenities(fromLines(record.amenities)), [record.amenities]);
  useEffect(() => setHlEn(fromLines(record.highlights_en)), [record.highlights_en]);
  useEffect(() => setHlRo(fromLines(record.highlights_ro)), [record.highlights_ro]);

  return (
    <section className="space-y-6" data-testid="step-6-content">
      <div><h2 className="font-editorial text-2xl mb-1">Description &amp; facilities</h2><p className="text-[13px] text-[var(--wr-ink-2)]">Full descriptions. Highlights and amenities improve search and filters.</p></div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Full description (Romanian)</span>
            <AiAssistButton field="full_description" locale="ro" context={context} onInsert={(v)=>setField("full_description_ro", v)} testid="ai-full-ro" />
          </div>
          <textarea rows={6} value={record.full_description_ro || ""} onChange={(e)=>setField("full_description_ro", e.target.value)} className={textareaCls} data-testid="field-full-ro" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Full description (English)</span>
            <AiAssistButton field="full_description" locale="en" context={context} onInsert={(v)=>setField("full_description_en", v)} testid="ai-full-en" />
          </div>
          <textarea rows={6} value={record.full_description_en || ""} onChange={(e)=>setField("full_description_en", e.target.value)} className={textareaCls} data-testid="field-full-en" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Highlights (Romanian) — one per line</span>
            <AiAssistButton field="highlights" locale="ro" context={context} onInsert={(v)=>{ setHlRo(v); setField("highlights_ro", toLines(v)); }} testid="ai-highlights-ro" />
          </div>
          <textarea rows={4} value={hlRo} onChange={(e)=>{ setHlRo(e.target.value); setField("highlights_ro", toLines(e.target.value)); }} className={textareaCls} data-testid="field-highlights-ro" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Highlights (English) — one per line</span>
            <AiAssistButton field="highlights" locale="en" context={context} onInsert={(v)=>{ setHlEn(v); setField("highlights_en", toLines(v)); }} testid="ai-highlights-en" />
          </div>
          <textarea rows={4} value={hlEn} onChange={(e)=>{ setHlEn(e.target.value); setField("highlights_en", toLines(e.target.value)); }} className={textareaCls} data-testid="field-highlights-en" />
        </div>

        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Amenities / facilities — one per line</span>
            <AiAssistButton field="amenities" locale="en" context={context} onInsert={(v)=>{ setAmenities(v); setField("amenities", toLines(v)); }} testid="ai-amenities" />
          </div>
          <textarea rows={4} value={amenities} onChange={(e)=>{ setAmenities(e.target.value); setField("amenities", toLines(e.target.value)); }} className={textareaCls} data-testid="field-amenities" />
        </div>

        <Field label="Best time to visit (EN)">
          <div className="flex gap-2">
            <input value={record.best_time_en || ""} onChange={(e)=>setField("best_time_en", e.target.value)} className={inputCls} data-testid="field-best-en" />
            <AiAssistButton field="best_time" locale="en" context={context} onInsert={(v)=>setField("best_time_en", v)} testid="ai-best-en" />
          </div>
        </Field>
        <Field label="Best time to visit (RO)">
          <div className="flex gap-2">
            <input value={record.best_time_ro || ""} onChange={(e)=>setField("best_time_ro", e.target.value)} className={inputCls} data-testid="field-best-ro" />
            <AiAssistButton field="best_time" locale="ro" context={context} onInsert={(v)=>setField("best_time_ro", v)} testid="ai-best-ro" />
          </div>
        </Field>

        <Field label="Accessibility notes (EN)">
          <div className="flex gap-2">
            <input value={record.accessibility_notes_en || ""} onChange={(e)=>setField("accessibility_notes_en", e.target.value)} className={inputCls} data-testid="field-access-en" />
            <AiAssistButton field="accessibility_notes" locale="en" context={context} onInsert={(v)=>setField("accessibility_notes_en", v)} testid="ai-access-en" />
          </div>
        </Field>
        <Field label="Accessibility notes (RO)">
          <div className="flex gap-2">
            <input value={record.accessibility_notes_ro || ""} onChange={(e)=>setField("accessibility_notes_ro", e.target.value)} className={inputCls} data-testid="field-access-ro" />
            <AiAssistButton field="accessibility_notes" locale="ro" context={context} onInsert={(v)=>setField("accessibility_notes_ro", v)} testid="ai-access-ro" />
          </div>
        </Field>

        <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={!!record.family_friendly} onChange={(e)=>setField("family_friendly", e.target.checked)} data-testid="field-family" /> Family-friendly</label>
        <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={!!record.pet_friendly} onChange={(e)=>setField("pet_friendly", e.target.checked)} data-testid="field-pet" /> Pet-friendly</label>
        <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={!!record.parking} onChange={(e)=>setField("parking", e.target.checked)} data-testid="field-parking" /> Parking available</label>
      </div>
    </section>
  );
}

function StepPricing({ record, setField }) {
  const priceType = record.price_type || "";
  return (
    <section className="space-y-6" data-testid="step-7-content">
      <div><h2 className="font-editorial text-2xl mb-1">Pricing (optional)</h2><p className="text-[13px] text-[var(--wr-ink-2)]">Leave empty if unknown. Choose a price mode below.</p></div>
      <div className="flex flex-wrap gap-2 mb-2">
        {["", "free", "paid", "from", "seasonal"].map(m => (
          <button key={m||"none"} type="button" onClick={()=>setField("price_type", m || null)}
            className={`px-3 py-2 border font-mono-wr text-[10px] uppercase tracking-widest ${priceType===m?"bg-[var(--wr-ink)] text-[var(--wr-bg)] border-[var(--wr-ink)]":"border-[var(--wr-border)] hover:border-[var(--wr-ink)]"}`}
            data-testid={`price-mode-${m||"none"}`}>
            {m || "no price"}
          </button>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Amount (numeric)"><input type="number" step="0.01" value={record.price_amount ?? ""} onChange={(e)=>setField("price_amount", e.target.value===""?null:Number(e.target.value))} className={inputCls} data-testid="field-price-amount" /></Field>
        <Field label="Currency"><input value={record.currency || "RON"} onChange={(e)=>setField("currency", e.target.value)} className={inputCls} data-testid="field-currency" /></Field>
        <Field label="Price text (Romanian)" hint="e.g. 'De la 350 RON/noapte'"><input value={record.price_text_ro || ""} onChange={(e)=>setField("price_text_ro", e.target.value)} className={inputCls} data-testid="field-price-text-ro" /></Field>
        <Field label="Price text (English)" hint="e.g. 'From 350 RON/night'"><input value={record.price_text_en || ""} onChange={(e)=>setField("price_text_en", e.target.value)} className={inputCls} data-testid="field-price-text-en" /></Field>
      </div>
    </section>
  );
}

function StepSEO({ record, setField, context }) {
  return (
    <section className="space-y-6" data-testid="step-8-content">
      <div>
        <h2 className="font-editorial text-2xl mb-1">SEO</h2>
        <p className="text-[13px] text-[var(--wr-ink-2)]">Slug and canonical URL are auto-generated. Titles &amp; descriptions boost search results.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Slug" hint="Auto-generated; edit only if you know what you're doing"><input value={record.slug || ""} onChange={(e)=>setField("slug", e.target.value)} className={inputCls} data-testid="field-slug" /></Field>
        <Field label="Canonical URL" hint="Auto-computed from slug &mdash; leave empty">
          <input value={record.canonical_url || ""} onChange={(e)=>setField("canonical_url", e.target.value)} placeholder={`/listings/${record.slug || ""}`} className={inputCls} data-testid="field-canonical" />
        </Field>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">SEO title (Romanian)</span>
            <AiAssistButton field="seo_title" locale="ro" context={context} onInsert={(v)=>setField("seo_title_ro", v)} testid="ai-seo-title-ro" />
          </div>
          <input value={record.seo_title_ro || ""} onChange={(e)=>setField("seo_title_ro", e.target.value)} className={inputCls} data-testid="field-seo-title-ro" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">SEO title (English)</span>
            <AiAssistButton field="seo_title" locale="en" context={context} onInsert={(v)=>setField("seo_title_en", v)} testid="ai-seo-title-en" />
          </div>
          <input value={record.seo_title_en || ""} onChange={(e)=>setField("seo_title_en", e.target.value)} className={inputCls} data-testid="field-seo-title-en" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Meta description (Romanian)</span>
            <AiAssistButton field="meta_description" locale="ro" context={context} onInsert={(v)=>setField("meta_description_ro", v)} testid="ai-meta-ro" />
          </div>
          <textarea rows={2} value={record.meta_description_ro || ""} onChange={(e)=>setField("meta_description_ro", e.target.value)} className={textareaCls} data-testid="field-meta-ro" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">Meta description (English)</span>
            <AiAssistButton field="meta_description" locale="en" context={context} onInsert={(v)=>setField("meta_description_en", v)} testid="ai-meta-en" />
          </div>
          <textarea rows={2} value={record.meta_description_en || ""} onChange={(e)=>setField("meta_description_en", e.target.value)} className={textareaCls} data-testid="field-meta-en" />
        </div>
        <Field label="OpenGraph image URL" hint="Optional — defaults to hero image"><input value={record.og_image_url || ""} onChange={(e)=>setField("og_image_url", e.target.value)} className={inputCls} data-testid="field-og" /></Field>
      </div>
    </section>
  );
}

function StepPublish({ record, onPublish, onArchive, onSaveDraft, onDelete, onGoto }) {
  const missing = [];
  if (!record.name_en) missing.push({ label: "Name EN", step: 3 });
  if (!record.name_ro) missing.push({ label: "Name RO", step: 3 });
  if (!record.listing_type) missing.push({ label: "Type", step: 1 });
  if (!record.region_slug) missing.push({ label: "Region", step: 2 });
  if (!record.hero_image_url) missing.push({ label: "Hero image", step: 5 });
  const bilingualGaps = [];
  if (record.short_description_en && !record.short_description_ro) bilingualGaps.push("Short desc RO");
  if (record.short_description_ro && !record.short_description_en) bilingualGaps.push("Short desc EN");
  if (record.full_description_en && !record.full_description_ro) bilingualGaps.push("Full desc RO");
  if (record.full_description_ro && !record.full_description_en) bilingualGaps.push("Full desc EN");

  return (
    <section className="space-y-6" data-testid="step-9-content">
      <div><h2 className="font-editorial text-2xl mb-1">Publish</h2><p className="text-[13px] text-[var(--wr-ink-2)]">Review, then save, publish, archive or delete.</p></div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="border border-[var(--wr-border)] p-5">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-2">Status</p>
          <p className="font-editorial text-2xl" data-testid="publish-current-status">{record.status || "draft"}</p>
        </div>
        <div className="border border-[var(--wr-border)] p-5">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-2">Type / Region</p>
          <p className="font-editorial text-xl">{TYPE_LABEL(record.listing_type || "—")} · {record.region_slug || "—"}</p>
        </div>
        <div className="border border-[var(--wr-border)] p-5">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-2">Slug</p>
          <p className="font-mono-wr text-[13px] break-all">{record.slug}</p>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="border border-[var(--wr-red)] p-4 bg-[var(--wr-surface-2)]" data-testid="publish-blockers">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)] mb-2">Required before publish</p>
          <ul className="space-y-1 text-[13px] text-[var(--wr-red)]">
            {missing.map(m => (
              <li key={m.label}>
                <button onClick={()=>onGoto(m.step)} className="underline hover:no-underline">{m.label} — go to step {m.step}</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {bilingualGaps.length > 0 && (
        <div className="border border-[var(--wr-border)] p-4 bg-[var(--wr-surface-2)]" data-testid="publish-warnings">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-2">Missing translations (public site will fall back)</p>
          <ul className="text-[13px] text-[var(--wr-ink-2)]">{bilingualGaps.map(g => <li key={g}>· {g}</li>)}</ul>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onSaveDraft} className="px-4 py-2.5 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest inline-flex items-center gap-2" data-testid="publish-save-draft">
          <Check size={14} /> Save as draft
        </button>
        <button type="button" onClick={onPublish} className="px-5 py-2.5 bg-[var(--wr-red)] text-white hover:bg-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-40" data-testid="publish-now" disabled={missing.length>0}>
          <Rocket size={14} /> Publish
        </button>
        <button type="button" onClick={onArchive} className="px-4 py-2.5 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest inline-flex items-center gap-2" data-testid="publish-archive">
          <Archive size={14} /> Archive
        </button>
        <button type="button" onClick={onDelete} className="ml-auto px-4 py-2.5 border border-[var(--wr-red)] text-[var(--wr-red)] hover:bg-[var(--wr-red)] hover:text-white font-mono-wr text-[10px] uppercase tracking-widest inline-flex items-center gap-2" data-testid="publish-delete">
          <Trash2 size={14} /> Move to Trash
        </button>
      </div>
    </section>
  );
}
